import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { selectAgroOverviewSelectedVariable } from 'redux/reducers/meteorologyAgroOverviewDashboardReducer';
import useOverviewKpis from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewKpis';
import useOverviewLocations from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewLocations';
import useOverviewMap from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewMap';
import useOverviewMapVariables from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewMapVariables';
import useOverviewMapError from 'hooks/Meteorology/Agro/Overview/Dashboard/useOverviewMapError';
import SummaryCards from './SummaryCards';
import VariableMap from './VariableMap';
import LocationsTable from './LocationsTable';
import ExportButton from './ExportButton';
import PartErrorState from './PartErrorState';

import './index.scss';

export default function Dashboard() {
  const selectedVariable = useSelector(selectAgroOverviewSelectedVariable);

  const kpis = useOverviewKpis();
  const table = useOverviewLocations();
  const map = useOverviewMap(selectedVariable);
  const mapVariablesPart = useOverviewMapVariables();
  const mapError = useOverviewMapError(map, mapVariablesPart);
  const { summary, isFetching: summaryFetching } = kpis;
  const { locations, total, isFetching: locationsFetching } = table;
  const { mapData, isFetching: mapFetching } = map;
  const { variables: mapVariables } = mapVariablesPart;

  // ESTADO VAZIO (§4 do contrato agro-overview.md): o predicado normativo é
  // tabela.total === 0 (undefined enquanto carrega → não é estado vazio).
  const isEmptyState = total === 0;

  return (
    <div className="agro-overview-dashboard">
      <Row className="align-items-center mb-3">
        <Col>
          <div className="dashboard-subtitle">Condições agrometeorológicas</div>
        </Col>
        <Col xs="auto">
          <ExportButton locations={locations ?? []} />
        </Col>
      </Row>

      {/* #2009 item 2: cada parte mostra a própria falha; as outras continuam. */}
      {kpis.errorKind ? (
        <PartErrorState
          label="Indicadores"
          subject="os indicadores"
          kind={kpis.errorKind}
          onRetry={kpis.retry}
        />
      ) : (
        <SummaryCards
          summary={summary}
          locations={locations ?? []}
          loading={summaryFetching && !summary}
          isEmptyState={isEmptyState}
        />
      )}

      {/* Decisão 10/08/2026: a coluna da tabela é dimensionada pelo CONTEÚDO
          (nomes de localidade nunca quebram linha) e o mapa cede o espaço. */}
      <Row className="g-3 mt-1 agro-overview-main-row">
        <Col xs={12} xl className="agro-overview-map-col">
          <VariableMap
            locations={locations ?? []}
            valuePoints={mapData?.pontos ?? []}
            variables={mapVariables ?? []}
            period={mapData ? { inicio: mapData.dataInicio, fim: mapData.dataFim } : null}
            loading={mapFetching}
            error={mapError.errorKind ? (
              <PartErrorState
                label="Mapa"
                subject="o mapa"
                kind={mapError.errorKind}
                onRetry={mapError.retry}
                className="agro-overview-part-error--tall flex-grow-1"
              />
            ) : undefined}
          />
        </Col>
        <Col xs={12} xl="auto" className="agro-overview-table-col">
          {table.errorKind ? (
            <PartErrorState
              label="Localidades monitoradas"
              subject="as localidades"
              kind={table.errorKind}
              onRetry={table.retry}
              className="agro-overview-part-error--tall"
            />
          ) : (
            <LocationsTable
              locations={locations ?? []}
              loading={locationsFetching && !locations}
              isEmptyState={isEmptyState}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}
